import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, AlertCircle, UserCheck, Coins, Music, Calendar, ShoppingCart, Shield } from 'lucide-react';

const TermsPage = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-[#060606] pt-48 pb-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#C9A84C]/20 mb-6">
            <FileText className="w-8 h-8 text-[#C9A84C]" />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-[#F5F0E8] mb-4">
            Conditions Générales d'Utilisation
          </h1>
          <p className="text-[#B9B2A8]">
            Dernière mise à jour : 10 février 2025
          </p>
        </div>

        {/* Introduction */}
        <div className="prose prose-invert max-w-none mb-12">
          <p className="text-[#B9B2A8] leading-relaxed">
            Bienvenue sur l'application Invasion Latina. En utilisant notre application, vous acceptez les présentes conditions générales d'utilisation. Veuillez les lire attentivement.
          </p>
        </div>

        {/* Content Sections */}
        <div className="space-y-8">
          {/* Section 1 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h2 className="font-display text-2xl text-[#C9A84C] mb-4">1. Acceptation des Conditions</h2>
            <p className="text-[#B9B2A8]">
              En téléchargeant, installant ou utilisant l'application Invasion Latina, vous acceptez d'être lié par ces conditions d'utilisation. Si vous n'acceptez pas ces conditions, veuillez ne pas utiliser l'application.
            </p>
          </section>

          {/* Section 2 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h2 className="font-display text-2xl text-[#C9A84C] mb-4">2. Description du Service</h2>
            <p className="text-[#B9B2A8] mb-4">Invasion Latina est une application mobile qui permet aux utilisateurs de :</p>
            <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
              <li>Consulter les événements et soirées à venir</li>
              <li>Acheter des billets (via redirection vers XCEED)</li>
              <li>Découvrir les DJs et demander des chansons</li>
              <li>Réserver des tables VIP</li>
              <li>Participer au programme de fidélité Invasion Coins</li>
              <li>Accéder aux galeries photos et aftermovies</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <UserCheck className="w-6 h-6 text-[#C9A84C] flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-display text-2xl text-[#C9A84C] mb-4">3. Inscription et Compte</h2>
                <p className="text-[#B9B2A8] mb-4">Pour accéder à certaines fonctionnalités, vous devez créer un compte. Vous vous engagez à :</p>
                <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
                  <li>Fournir des informations exactes et à jour</li>
                  <li>Maintenir la confidentialité de votre mot de passe</li>
                  <li>Nous informer immédiatement de toute utilisation non autorisée de votre compte</li>
                  <li>Être responsable de toutes les activités effectuées sous votre compte</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 4 */}
          <section className="bg-gradient-to-br from-red-500/20 to-transparent border border-red-500/30 rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-display text-2xl text-[#F5F0E8] mb-4">4. Conditions d'Âge</h2>
                <p className="text-[#F5F0E8] font-medium mb-2">⚠️ Important</p>
                <p className="text-[#B9B2A8]">
                  Cette application est destinée aux personnes âgées de <strong className="text-[#F5F0E8]">17 ans et plus</strong>. En utilisant l'application, vous confirmez avoir l'âge légal requis pour participer à des événements de vie nocturne en Belgique.
                </p>
              </div>
            </div>
          </section>

          {/* Section 5 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <Coins className="w-6 h-6 text-[#C9A84C] flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-display text-2xl text-[#C9A84C] mb-4">5. Programme de Fidélité - Invasion Coins</h2>
                <p className="text-[#B9B2A8] mb-4">Le programme Invasion Coins est soumis aux règles suivantes :</p>
                <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
                  <li>Les points sont attribués lors de votre présence physique aux événements</li>
                  <li>La vérification se fait par scan de QR code et/ou géolocalisation</li>
                  <li><strong className="text-[#F5F0E8]">25 Invasion Coins = 1 entrée gratuite pour un ami</strong></li>
                  <li>Les points n'ont aucune valeur monétaire et ne sont pas transférables</li>
                  <li>Invasion Latina se réserve le droit de modifier ou d'annuler le programme à tout moment</li>
                  <li>Toute tentative de fraude entraînera l'annulation des points et la suspension du compte</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 6 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <Music className="w-6 h-6 text-[#C9A84C] flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-display text-2xl text-[#C9A84C] mb-4">6. Demandes de Chansons</h2>
                <p className="text-[#B9B2A8] mb-4">La fonctionnalité de demande de chansons est soumise aux conditions suivantes :</p>
                <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
                  <li>Les demandes ne sont disponibles que pendant les événements</li>
                  <li>La vérification de présence par géolocalisation peut être requise</li>
                  <li>Les DJs se réservent le droit de jouer ou non les chansons demandées</li>
                  <li>Aucune garantie n'est donnée quant à la diffusion effective d'une chanson demandée</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 7 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <Calendar className="w-6 h-6 text-[#C9A84C] flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-display text-2xl text-[#C9A84C] mb-4">7. Réservation de Tables</h2>
                <p className="text-[#B9B2A8] mb-4">Les réservations de tables sont soumises aux conditions suivantes :</p>
                <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
                  <li>Les demandes de réservation sont transmises via WhatsApp</li>
                  <li>La confirmation finale dépend de la disponibilité</li>
                  <li>Les prix indiqués sont les consommations minimums requises</li>
                  <li>Les conditions d'annulation sont communiquées lors de la confirmation</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 8 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <ShoppingCart className="w-6 h-6 text-[#C9A84C] flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-display text-2xl text-[#C9A84C] mb-4">8. Achat de Billets</h2>
                <p className="text-[#B9B2A8] mb-4">
                  L'achat de billets s'effectue via notre partenaire XCEED. En utilisant ce service :
                </p>
                <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
                  <li>Vous êtes redirigé vers la plateforme XCEED</li>
                  <li>Les conditions de vente de XCEED s'appliquent</li>
                  <li>Invasion Latina n'est pas responsable des transactions effectuées sur XCEED</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 9 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-[#C9A84C] flex-shrink-0 mt-1" />
              <div>
                <h2 className="font-display text-2xl text-[#C9A84C] mb-4">9. Comportement de l'Utilisateur</h2>
                <p className="text-[#B9B2A8] mb-4">Vous vous engagez à ne pas :</p>
                <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
                  <li>Utiliser l'application à des fins illégales</li>
                  <li>Tenter de falsifier votre localisation ou vos données</li>
                  <li>Créer plusieurs comptes pour contourner les limitations</li>
                  <li>Publier du contenu offensant, discriminatoire ou illégal</li>
                  <li>Tenter de pirater ou de compromettre la sécurité de l'application</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Section 10 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h2 className="font-display text-2xl text-[#C9A84C] mb-4">10. Propriété Intellectuelle</h2>
            <p className="text-[#B9B2A8]">
              Tous les contenus de l'application (logos, images, textes, design) sont la propriété d'Invasion Latina ou de ses partenaires. Toute reproduction non autorisée est interdite.
            </p>
          </section>

          {/* Section 11 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h2 className="font-display text-2xl text-[#C9A84C] mb-4">11. Limitation de Responsabilité</h2>
            <p className="text-[#B9B2A8] mb-4">Invasion Latina ne peut être tenu responsable :</p>
            <ul className="list-disc list-inside text-[#B9B2A8] space-y-2">
              <li>Des interruptions temporaires du service</li>
              <li>Des erreurs techniques ou bugs</li>
              <li>Des décisions artistiques des DJs</li>
              <li>Des problèmes liés aux services tiers (XCEED, etc.)</li>
              <li>De l'utilisation frauduleuse de votre compte par un tiers</li>
            </ul>
          </section>

          {/* Section 15 */}
          <section className="bg-[#0B0B0C] border border-white/10 rounded-xl p-6 sm:p-8">
            <h2 className="font-display text-2xl text-[#C9A84C] mb-4">15. Droit Applicable</h2>
            <p className="text-[#B9B2A8]">
              Les présentes conditions sont régies par le droit belge. Tout litige sera soumis à la compétence exclusive des tribunaux de Bruxelles, Belgique.
            </p>
          </section>

          {/* Contact */}
          <section className="bg-gradient-to-br from-[#C9A84C]/20 to-transparent border border-[#C9A84C]/30 rounded-xl p-6 sm:p-8">
            <h3 className="font-display text-xl text-[#F5F0E8] mb-4">📧 Contact</h3>
            <p className="text-[#B9B2A8] mb-4">Pour toute question concernant ces conditions :</p>
            <ul className="space-y-2 text-[#B9B2A8]">
              <li><strong className="text-[#F5F0E8]">Email :</strong> <a href="mailto:info@invasionlatina.be" className="text-[#C9A84C] hover:underline">info@invasionlatina.be</a></li>
              <li><strong className="text-[#F5F0E8]">Site web :</strong> <a href="https://www.invasionlatina.be" className="text-[#C9A84C] hover:underline">www.invasionlatina.be</a></li>
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;